import type { GameStatus } from '@brick-rogue/game-core';

export type GamePlatform = 'standalone' | 'poki' | 'crazygames';

export interface PlatformRuntimeHooks {
  readonly setAudioMuted: (muted: boolean) => void;
  readonly setSuspended: (suspended: boolean) => void;
}

export interface PlatformSdkHooks {
  readonly setPlatformAudioMuted: (muted: boolean) => void;
}

export interface PlatformAdHooks {
  readonly onStarted: () => void;
  readonly onFinished: () => void;
}

export interface PlatformAdapter {
  readonly platform: GamePlatform;
  initialize: (hooks: PlatformSdkHooks) => Promise<void>;
  loadingStart: () => void;
  loadingFinished: () => void;
  gameplayStart: () => void;
  gameplayStop: () => void;
  requestCommercialBreak: (hooks: PlatformAdHooks) => Promise<void>;
  requestRewardedBreak: (hooks: PlatformAdHooks) => Promise<boolean>;
  destroy: () => void;
}

export interface PokiSdk {
  readonly init: () => Promise<unknown>;
  readonly gameLoadingFinished: () => void;
  readonly gameplayStart: () => void;
  readonly gameplayStop: () => void;
  readonly commercialBreak: (onStart?: () => void) => Promise<unknown>;
  readonly rewardedBreak: (options: {
    readonly size: 'small' | 'medium' | 'large';
    readonly onStart?: () => void;
  }) => Promise<boolean>;
}

export interface CrazyGamesSettings {
  readonly muteAudio?: boolean;
  readonly disableChat?: boolean;
}

export interface CrazyGamesSdk {
  readonly init: () => Promise<unknown>;
  readonly game: {
    readonly settings: CrazyGamesSettings;
    readonly loadingStart: () => void;
    readonly loadingStop: () => void;
    readonly gameplayStart: () => void;
    readonly gameplayStop: () => void;
    readonly addSettingsChangeListener: (listener: (settings: CrazyGamesSettings) => void) => void;
    readonly removeSettingsChangeListener: (
      listener: (settings: CrazyGamesSettings) => void,
    ) => void;
  };
  readonly ad: {
    readonly requestAd: (
      kind: 'midgame' | 'rewarded',
      callbacks: {
        readonly adStarted: () => void;
        readonly adFinished: () => void;
        readonly adError: (error: unknown) => void;
      },
    ) => void;
  };
}

export interface PlatformSdkGlobals {
  readonly PokiSDK?: PokiSdk;
  readonly CrazyGames?: {
    readonly SDK?: CrazyGamesSdk;
  };
}

export function resolveGamePlatform(mode: string): GamePlatform {
  if (mode === 'poki') {
    return 'poki';
  }
  if (mode === 'crazygames' || mode === 'crazygame') {
    return 'crazygames';
  }
  return 'standalone';
}

export function isHostedGamePlatform(platform: GamePlatform): boolean {
  return platform !== 'standalone';
}

export function createPlatformAdapter(
  platform: GamePlatform,
  sdkGlobals: PlatformSdkGlobals = globalThis as unknown as PlatformSdkGlobals,
): PlatformAdapter {
  if (platform === 'poki') {
    return new PokiPlatformAdapter(sdkGlobals.PokiSDK);
  }
  if (platform === 'crazygames') {
    return new CrazyGamesPlatformAdapter(sdkGlobals.CrazyGames?.SDK);
  }
  return new StandalonePlatformAdapter();
}

export class PlatformGameSession {
  readonly #adapter: PlatformAdapter;
  readonly #runtimeHooks: PlatformRuntimeHooks;
  #initializePromise: Promise<void> | null = null;
  #loadingWasFinished = false;
  #wasGameplayActive = false;
  #platformAudioMuted = false;
  #adPlaying = false;
  #breakPromise: Promise<boolean> | null = null;
  #destroyed = false;

  constructor(adapter: PlatformAdapter, runtimeHooks: PlatformRuntimeHooks) {
    this.#adapter = adapter;
    this.#runtimeHooks = runtimeHooks;
  }

  get platform(): GamePlatform {
    return this.#adapter.platform;
  }

  initialize(): Promise<void> {
    if (this.#initializePromise) {
      return this.#initializePromise;
    }

    this.#runtimeHooks.setAudioMuted(false);
    this.#initializePromise = this.#adapter
      .initialize({
        setPlatformAudioMuted: (muted) => {
          if (this.#destroyed) {
            return;
          }
          this.#platformAudioMuted = muted;
          this.#syncAudioMute();
        },
      })
      .then(() => {
        if (!this.#destroyed) {
          this.#adapter.loadingStart();
        }
      });
    return this.#initializePromise;
  }

  loadingFinished(): void {
    if (this.#destroyed || this.#loadingWasFinished) {
      return;
    }
    this.#loadingWasFinished = true;
    this.#adapter.loadingFinished();
  }

  syncStatus(status: GameStatus): void {
    if (this.#destroyed) {
      return;
    }

    const isGameplayActive = status === 'aiming' || status === 'playing';
    if (isGameplayActive === this.#wasGameplayActive) {
      return;
    }

    this.#wasGameplayActive = isGameplayActive;
    if (isGameplayActive) {
      this.#adapter.gameplayStart();
    } else {
      this.#adapter.gameplayStop();
    }
  }

  async requestCommercialBreak(): Promise<void> {
    await this.#runBreak((hooks) => this.#adapter.requestCommercialBreak(hooks));
  }

  requestRewardedBreak(): Promise<boolean> {
    return this.#runBreak((hooks) => this.#adapter.requestRewardedBreak(hooks));
  }

  destroy(): void {
    if (this.#destroyed) {
      return;
    }
    this.#destroyed = true;
    if (this.#wasGameplayActive) {
      this.#adapter.gameplayStop();
      this.#wasGameplayActive = false;
    }
    if (this.#adPlaying) {
      this.#adPlaying = false;
      this.#runtimeHooks.setSuspended(false);
      this.#syncAudioMute();
    }
    this.#adapter.destroy();
  }

  async #runBreak(request: (hooks: PlatformAdHooks) => Promise<void | boolean>): Promise<boolean> {
    if (this.#destroyed) {
      return false;
    }
    if (this.#breakPromise) {
      await this.#breakPromise;
      return false;
    }

    let started = false;
    const onStarted = () => {
      if (started || this.#destroyed) {
        return;
      }
      started = true;
      this.#adPlaying = true;
      this.#runtimeHooks.setSuspended(true);
      this.#syncAudioMute();
    };
    const onFinished = () => {
      if (!started) {
        return;
      }
      started = false;
      if (this.#destroyed) {
        return;
      }
      this.#adPlaying = false;
      this.#runtimeHooks.setSuspended(false);
      this.#syncAudioMute();
    };

    const pendingBreak = Promise.resolve()
      .then(() => request({ onStarted, onFinished }))
      .then((result) => result === true)
      .catch(() => false)
      .finally(onFinished);
    this.#breakPromise = pendingBreak;
    try {
      return await pendingBreak;
    } finally {
      if (this.#breakPromise === pendingBreak) {
        this.#breakPromise = null;
      }
    }
  }

  #syncAudioMute(): void {
    this.#runtimeHooks.setAudioMuted(this.#platformAudioMuted || this.#adPlaying);
  }
}

class StandalonePlatformAdapter implements PlatformAdapter {
  readonly platform = 'standalone';

  async initialize(): Promise<void> {}
  loadingStart(): void {}
  loadingFinished(): void {}
  gameplayStart(): void {}
  gameplayStop(): void {}
  async requestCommercialBreak(): Promise<void> {}
  async requestRewardedBreak(): Promise<boolean> {
    return false;
  }
  destroy(): void {}
}

class PokiPlatformAdapter implements PlatformAdapter {
  readonly platform = 'poki';
  readonly #sdk: PokiSdk | undefined;
  #available = false;

  constructor(sdk: PokiSdk | undefined) {
    this.#sdk = sdk;
  }

  async initialize(): Promise<void> {
    if (!this.#sdk) {
      return;
    }
    this.#available = true;
    try {
      await this.#sdk.init();
    } catch {
      // Poki recommends continuing to load the game when SDK initialization fails.
    }
  }

  loadingStart(): void {}

  loadingFinished(): void {
    this.#call(() => this.#sdk?.gameLoadingFinished());
  }

  gameplayStart(): void {
    this.#call(() => this.#sdk?.gameplayStart());
  }

  gameplayStop(): void {
    this.#call(() => this.#sdk?.gameplayStop());
  }

  async requestCommercialBreak(hooks: PlatformAdHooks): Promise<void> {
    if (!this.#available || !this.#sdk) {
      return;
    }
    let started = false;
    try {
      await this.#sdk.commercialBreak(() => {
        started = true;
        hooks.onStarted();
      });
    } catch {
      // An unavailable advertisement must never block the game flow.
    } finally {
      if (started) {
        hooks.onFinished();
      }
    }
  }

  async requestRewardedBreak(hooks: PlatformAdHooks): Promise<boolean> {
    if (!this.#available || !this.#sdk) {
      return false;
    }
    let started = false;
    try {
      return await this.#sdk.rewardedBreak({
        size: 'medium',
        onStart: () => {
          started = true;
          hooks.onStarted();
        },
      });
    } catch {
      return false;
    } finally {
      if (started) {
        hooks.onFinished();
      }
    }
  }

  destroy(): void {}

  #call(callback: () => void): void {
    if (!this.#available) {
      return;
    }
    try {
      callback();
    } catch {
      // Platform telemetry must not interrupt gameplay.
    }
  }
}

class CrazyGamesPlatformAdapter implements PlatformAdapter {
  readonly platform = 'crazygames';
  readonly #sdk: CrazyGamesSdk | undefined;
  #available = false;
  #settingsListener: ((settings: CrazyGamesSettings) => void) | null = null;

  constructor(sdk: CrazyGamesSdk | undefined) {
    this.#sdk = sdk;
  }

  async initialize(hooks: PlatformSdkHooks): Promise<void> {
    if (!this.#sdk) {
      return;
    }
    try {
      await this.#sdk.init();
    } catch {
      return;
    }

    this.#available = true;
    this.#settingsListener = (settings) => hooks.setPlatformAudioMuted(settings.muteAudio === true);
    hooks.setPlatformAudioMuted(this.#sdk.game.settings.muteAudio === true);
    this.#call(() => this.#sdk?.game.addSettingsChangeListener(this.#settingsListener!));
  }

  loadingStart(): void {
    this.#call(() => this.#sdk?.game.loadingStart());
  }

  loadingFinished(): void {
    this.#call(() => this.#sdk?.game.loadingStop());
  }

  gameplayStart(): void {
    this.#call(() => this.#sdk?.game.gameplayStart());
  }

  gameplayStop(): void {
    this.#call(() => this.#sdk?.game.gameplayStop());
  }

  async requestCommercialBreak(hooks: PlatformAdHooks): Promise<void> {
    await this.#requestAd('midgame', hooks);
  }

  requestRewardedBreak(hooks: PlatformAdHooks): Promise<boolean> {
    return this.#requestAd('rewarded', hooks);
  }

  destroy(): void {
    const listener = this.#settingsListener;
    if (listener) {
      this.#call(() => this.#sdk?.game.removeSettingsChangeListener(listener));
      this.#settingsListener = null;
    }
    this.#available = false;
  }

  #requestAd(kind: 'midgame' | 'rewarded', hooks: PlatformAdHooks): Promise<boolean> {
    const sdk = this.#sdk;
    if (!this.#available || !sdk) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      let started = false;
      let settled = false;
      const finish = (completed: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        if (started) {
          hooks.onFinished();
        }
        resolve(completed);
      };

      try {
        sdk.ad.requestAd(kind, {
          adStarted: () => {
            if (!started) {
              started = true;
              hooks.onStarted();
            }
          },
          adFinished: () => finish(true),
          adError: () => finish(false),
        });
      } catch {
        finish(false);
      }
    });
  }

  #call(callback: () => void): void {
    if (!this.#available) {
      return;
    }
    try {
      callback();
    } catch {
      // Platform telemetry must not interrupt gameplay.
    }
  }
}
